import { Typography, MenuItem, Menu, Divider, ListItem, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';

import usePreferencesMenuStyles from './styles';
import { UserPreference, PreferencesMenuProps, PreferencesMenuRefProps } from './types';
import { useContextMenu } from 'kineticUtils/hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import PreferencesMenuItem from './PreferencesMenuItem';

const PreferencesMenu: ForwardRefExoticComponent<
  PreferencesMenuProps & RefAttributes<PreferencesMenuRefProps>
> = forwardRef((props, ref) => {
  const { t } = useTranslation('common');
  const {
    setPreference,
    savePreference,
    updatePreference,
    toggleDefault,
    preferences,
    deletePreference,
    title,
    container,
  } = props;
  const classes = usePreferencesMenuStyles(props);

  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);

  const {
    props: { getContentAnchorEl: _, ...menuProps },
    handleOpenMenu: openMenu,
    handleCloseMenu: closeMenu,
  } = useContextMenu();

  useImperativeHandle(ref, () => ({
    onOpenMenuButtonClick(event) {
      menuProps.open && closeMenu();
      !menuProps.open && openMenu(event);
    },
  }));

  const renderMenuItem = useCallback(
    (preference: UserPreference) => {
      return (
        <PreferencesMenuItem
          key={preference.id}
          preference={preference}
          savePreference={savePreference}
          updatePreference={updatePreference}
          deletePreference={deletePreference}
          setPreference={setPreference}
          toggleDefault={toggleDefault}
          closeMenu={closeMenu}
          title={title}
          removeCreateMode={() => setIsCreateMode(false)}
          container={container}
        />
      );
    },
    [
      closeMenu,
      container,
      deletePreference,
      savePreference,
      setPreference,
      title,
      toggleDefault,
      updatePreference,
    ]
  );

  const prefs = Object.entries(preferences)
    .map(([key, value]) => ({
      ...value,
      id: key,
    }))
    .filter((pref) => pref.id !== 'updatedAt');

  const menuItems = useMemo(() => {
    return prefs ? prefs.map(renderMenuItem) : null;
  }, [prefs, renderMenuItem]);

  const hasSavedPreferences = !!(prefs && prefs.length);

  const renderPreferencesMenu = () => {
    return (
      <>
        <Menu
          id='PrefMenu'
          {...menuProps}
          onClose={closeMenu}
          onKeyDown={(e) => {
            // esc also hits cancel on the title
            const isEsc = e.key === 'Escape';
            if (!isCreateMode && isEsc) {
              closeMenu();
            }
          }}
          container={container}
        >
          <ListItem>
            <Typography variant='body1' fontWeight='bold'>
              {t('saved', { title })}
            </Typography>
          </ListItem>
          <Divider />
          <div className={classes.menuItemsWrapper}>
            {hasSavedPreferences || isCreateMode ? (
              menuItems
            ) : (
              <MenuItem disabled>{t('noSaved', { title })}</MenuItem>
            )}
            {isCreateMode &&
              renderMenuItem({
                name: '',
              })}
          </div>
          <Divider />
          <MenuItem disabled={isCreateMode} onClick={() => setIsCreateMode(true)}>
            <ListItemText>{t('saveCurrent')}</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  };

  return renderPreferencesMenu();
});

export default PreferencesMenu;
